/**
 * Currency symbols, and money formatted with one.
 *
 * Figures were reading "GBP 8,122,680" in places and "£8,122,680" in others,
 * because the charts had a symbol map and nothing else did. Same model, same
 * number, two presentations.
 *
 * The map lives here rather than in charts/tokens.ts so that a view which has
 * nothing to do with charts does not have to import chart design tokens to
 * format a figure - and, more to the point, so there is one map. charts/tokens
 * re-exports from here, which keeps v1's charts on exactly the values they
 * already used.
 *
 * Base currency is a FREE TEXT field in settings, so an unknown code is not an
 * edge case, it is the normal path for anything outside this list. Those fall
 * back to the code and a space, which is what "AED 40,000" already looked
 * like and reads correctly.
 */

const SYMBOLS: Record<string, string> = {
  GBP: '£',
  EUR: '€',
  USD: '$',
  JPY: '¥',
  CNY: '¥',
  INR: '₹',
  CHF: 'CHF ',
  AUD: 'A$',
  CAD: 'C$',
  NZD: 'NZ$',
  SGD: 'S$',
  HKD: 'HK$',
  ZAR: 'R',
  SEK: 'kr ',
  NOK: 'kr ',
  DKK: 'kr ',
  PLN: 'zł ',
  // The dirham's own sign is very poorly supported in the fonts this app
  // loads, so the code is the legible choice rather than a lazy one. Same for
  // the other Gulf currencies.
  AED: 'AED ',
  SAR: 'SAR ',
  QAR: 'QAR ',
};

/**
 * The prefix for a currency: a symbol where one renders, else the code.
 *
 * Always safe to concatenate - the code forms carry their own trailing space.
 */
export function currencySymbol(code: string | undefined): string {
  if (!code) return '';
  return SYMBOLS[code.toUpperCase()] ?? `${code} `;
}

/**
 * A money figure, with its symbol and a sign in front of the symbol.
 *
 * Negative reads "-£1,234", not "£-1,234": the minus belongs to the amount,
 * not to the currency.
 */
export function formatMoney(
  value: number | undefined,
  code: string | undefined,
  options: { decimals?: number } = {},
): string {
  const n = value ?? 0;
  const sign = n < 0 ? '-' : '';
  const decimals = options.decimals ?? 0;
  return `${sign}${currencySymbol(code)}${Math.abs(n).toLocaleString('en-GB', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}
